/** A body seen from a fixed three-quarter angle, drawn eight ways so its heading reads at a glance. */

const EMOJI: Record<string, string> = {
  wave: "\u{1F44B}",
  heart: "\u{2764}\u{FE0F}",
  laugh: "\u{1F602}",
};

/** Where the heading marker points on screen, in degrees clockwise from up. North is up. */
const TURN = [0, 45, 90, 135, 180, 225, 270, 315];

/** Whether a face is drawn: a body walking away from the viewer is seen from behind. */
const FACING_US = [false, false, true, true, true, true, true, false];

/** How much of the body's width the angle foreshortens, so a side-on body is narrower. */
const WIDTH = [0.62, 0.72, 0.86, 0.96, 1, 0.96, 0.86, 0.72];

export interface AvatarSpriteProps {
  /** An index into the eight directions, clockwise from north. */
  readonly facing: number;
  readonly colour: string;
  /** Drawn above the head while it plays. */
  readonly emote: string | null;
  /** Pixels from crown to feet. */
  readonly height: number;
  readonly label?: string;
}

export function AvatarSprite({ facing, colour, emote, height, label }: AvatarSpriteProps) {
  const index = Math.min(7, Math.max(0, Math.round(facing)));
  const turn = TURN[index] ?? 0;
  const squash = WIDTH[index] ?? 1;
  const width = height * 0.6;

  return (
    <div style={{ position: "relative", width, height, pointerEvents: "none" }}>
      {emote !== null && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: -height * 0.42,
            transform: "translateX(-50%)",
            fontSize: height * 0.36,
            lineHeight: 1,
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
          }}
        >
          {EMOJI[emote] ?? emote}
        </div>
      )}
      <svg
        viewBox="0 0 60 100"
        width={width}
        height={height}
        role="img"
        aria-label={label ?? `a body facing ${index}`}
      >
        <ellipse cx="30" cy="96" rx={16 * squash} ry="4" fill="rgba(0,0,0,0.35)" />
        <rect
          x={30 - 15 * squash}
          y="38"
          width={30 * squash}
          height="42"
          rx={10 * squash}
          fill={colour}
        />
        <rect x="22" y="78" width="7" height="18" rx="3" fill="#22262c" />
        <rect x="31" y="78" width="7" height="18" rx="3" fill="#22262c" />
        <g transform={`rotate(${turn} 30 24)`}>
          <path d="M30 2 L37 12 L23 12 Z" fill={colour} stroke="rgba(0,0,0,0.3)" strokeWidth="1" />
        </g>
        <circle cx="30" cy="24" r="14" fill="#f0d3b4" stroke="rgba(0,0,0,0.25)" strokeWidth="1" />
        {FACING_US[index] === true ? (
          <>
            <circle cx={30 - 6 * squash} cy="22" r="2.2" fill="#22262c" />
            <circle cx={30 + 6 * squash} cy="22" r="2.2" fill="#22262c" />
            <path
              d={`M${30 - 6 * squash} 30 Q30 34 ${30 + 6 * squash} 30`}
              stroke="#22262c"
              strokeWidth="2"
              fill="none"
            />
          </>
        ) : (
          <path d="M18 20 Q30 10 42 20 L42 14 Q30 6 18 14 Z" fill="#4a3b2f" />
        )}
      </svg>
    </div>
  );
}
